import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import PopupState, { bindTrigger, bindMenu } from 'material-ui-popup-state'

import useMediaQuery from '@mui/material/useMediaQuery'
import { alpha, styled, useTheme } from '@mui/material/styles'
import Button from '@mui/material/Button'
import LoadingButton from '@mui/lab/LoadingButton'
import Menu from '@mui/material/Menu'
import MenuItem from '@mui/material/MenuItem'
import Stack from '@mui/material/Stack'

import DeleteRoundedIcon from '@mui/icons-material/DeleteRounded'
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown'
import MonitorHeartRoundedIcon from '@mui/icons-material/MonitorHeartRounded'
import SyncIcon from '@mui/icons-material/Sync'

import { OfflinePatient } from '@/core/offline'
import DeleteConfirmationDialog from '@/components/ui/dialogs/DeleteConfirmationDialog'
import { useOfflineMode } from '@/providers/OfflineModeProvider'

import { useStartLocalConsultation } from '../model/useStartLocalConsultation'
import { useDeletePatient } from '../model/useDeletePatient'
import { useSyncPatient } from '../model/useSyncPatient'

const StyledMenu = styled(Menu)(({ theme }) => ({
  '& .MuiPaper-root': {
    borderRadius: 6,
    marginTop: theme.spacing(1),
    minWidth: 180,
    color: theme.palette.mode === 'light' ? 'rgb(55, 65, 81)' : theme.palette.grey[300],
    boxShadow:
      'rgb(255, 255, 255) 0px 0px 0px 0px, rgba(0, 0, 0, 0.05) 0px 0px 0px 1px, rgba(0, 0, 0, 0.1) 0px 10px 15px -3px, rgba(0, 0, 0, 0.05) 0px 4px 6px -2px',
    '& .MuiMenu-list': {
      padding: '4px 0',
    },
    '& .MuiMenuItem-root': {
      '& .MuiSvgIcon-root': {
        fontSize: 18,
        color: theme.palette.text.secondary,
        marginRight: theme.spacing(1.5),
      },
      '&:active': {
        backgroundColor: alpha(theme.palette.primary.main, theme.palette.action.selectedOpacity),
      },
    },
  },
}))

type OfflineActionsProps = {
  patient: OfflinePatient
  onPatientDeleted?: () => void
}

export function OfflineActions(props: OfflineActionsProps) {
  const { patient, onPatientDeleted } = props
  const { t } = useTranslation()

  const theme = useTheme()
  const isLargeScreen = useMediaQuery(theme.breakpoints.up('lg'), { noSsr: true })

  const [openDeleteConfirmationDialog, setOpenDeleteConfirmationDialog] = useState(false)

  const { isOfflineMode } = useOfflineMode()

  const [startLocalConsultation, isStartingConsultation] = useStartLocalConsultation()
  const [deletePatient, isDeletingPatient] = useDeletePatient()
  const [syncPatient, isSyncing] = useSyncPatient()

  const showSyncOption = !isOfflineMode && patient._syncStatus === 'pending'

  const toggleDeleteConfirmationDialog = () => {
    setOpenDeleteConfirmationDialog((prevOpen) => !prevOpen)
  }

  return (
    <>
      {isLargeScreen ? (
        <Stack direction="row" spacing={2}>
          <LoadingButton id="start-local" variant="contained" color="success" loading={isStartingConsultation} onClick={() => startLocalConsultation(patient)}>
            {t('page.infoPatient.localConsultationButton.label')}
          </LoadingButton>
          {showSyncOption && (
            <LoadingButton variant="contained" color="info" loading={isSyncing} onClick={() => syncPatient(patient)} startIcon={<SyncIcon />}>
              {t('Sync Patient')}
            </LoadingButton>
          )}
          <Button color="error" disableElevation onClick={toggleDeleteConfirmationDialog} variant="contained">
            {t('common:button.delete.label')}
          </Button>
        </Stack>
      ) : (
        <PopupState variant="popover" popupId="more-button">
          {(popupState) => (
            <>
              <Button variant="contained" disableElevation endIcon={<KeyboardArrowDownIcon />} {...bindTrigger(popupState)}>
                {t('common:button.action.label')}
              </Button>
              <StyledMenu
                {...bindMenu(popupState)}
                anchorOrigin={{
                  vertical: 'bottom',
                  horizontal: 'right',
                }}
                transformOrigin={{
                  vertical: 'top',
                  horizontal: 'right',
                }}
              >
                <MenuItem
                  onClick={() => {
                    startLocalConsultation(patient)
                    popupState.close()
                  }}
                  disableRipple
                  disabled={isStartingConsultation}
                >
                  <MonitorHeartRoundedIcon />
                  {t('page.infoPatient.localConsultationButton.label')}
                </MenuItem>
                {showSyncOption && (
                  <MenuItem
                    onClick={() => {
                      syncPatient(patient)
                      popupState.close()
                    }}
                    disableRipple
                    disabled={isSyncing}
                  >
                    <SyncIcon />
                    {t('Sync Patient')}
                  </MenuItem>
                )}
                <MenuItem
                  onClick={() => {
                    toggleDeleteConfirmationDialog()
                    popupState.close()
                  }}
                  disableRipple
                >
                  <DeleteRoundedIcon />
                  {t('common:button.delete.label')}
                </MenuItem>
              </StyledMenu>
            </>
          )}
        </PopupState>
      )}
      <DeleteConfirmationDialog
        isDeleting={isDeletingPatient}
        maxWidth={700}
        onClose={toggleDeleteConfirmationDialog}
        onDelete={() => deletePatient(patient._id, onPatientDeleted)}
        open={openDeleteConfirmationDialog}
        title={t('dialog.deleteConfirmation.patient.title')}
        message={t('dialog.deleteConfirmation.patient.message')}
      />
    </>
  )
}
